import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import AccueilFileAttente from '@/components/feature/AccueilFileAttente';

type SectionKey = 'overview' | 'personnel' | 'patients' | 'activite' | 'systeme' | 'file-attente';

interface NavItem {
  key: SectionKey;
  label: string;
  description: string;
}

interface ActionItem {
  title: string;
  description: string;
  route: string;
}

const SECTIONS: NavItem[] = [
  { key: 'overview', label: 'Vue d\'ensemble', description: 'Statistiques et accès rapides' },
  { key: 'personnel', label: 'Personnel', description: 'Médecins, secrétaires et équipe soignante' },
  { key: 'patients', label: 'Patients & Données', description: 'Dossiers, imports et corrections' },
  { key: 'activite', label: 'Activité', description: 'Rendez-vous, suivis et contenu' },
  { key: 'systeme', label: 'Système', description: 'Statistiques, configuration et sécurité' },
  { key: 'file-attente', label: 'Salle d\'attente', description: 'Ordre de passage en temps réel' },
];

export default function AdminPage() {
  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<SectionKey>('overview');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRdv: 0,
    rdvTermines: 0,
    rdvEnAttente: 0,
    totalPatients: 0,
    totalMedecins: 0,
  });
  const [securityResetModalOpen, setSecurityResetModalOpen] = useState(false);
  const [securityResetLoading, setSecurityResetLoading] = useState(false);
  const [securityResetResult, setSecurityResetResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      if (role === 'super_admin' || role === 'admin') {
        setIsSuperAdmin(role === 'super_admin' || userRoles.includes('super_admin'));
        setIsAdminUser(true);
        setLoading(false);
        return;
      }

      if (role === 'comptable') {
        navigate('/admin/video-meetings', { replace: true });
        return;
      }

      const { data, error } = await supabase
        .from('super_admins')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error || !data) {
        navigate('/', { replace: true });
        return;
      }

      setIsSuperAdmin(data.role === 'super_admin');

      if (data.role === 'comptable') {
        navigate('/admin/video-meetings', { replace: true });
        return;
      }

      setIsAdminUser(true);
      setLoading(false);
    };

    checkAccess();
  }, [user, role, userRoles, authLoading, navigate]);

  useEffect(() => {
    if (isAdminUser && !authLoading) {
      loadStats();
    }
  }, [isAdminUser, authLoading]);

  const loadStats = async () => {
    try {
      const { data: rdvData } = await supabase.from('rendez_vous').select('id, statut');
      const { data: patientsData } = await supabase.from('patients').select('id');
      const { data: medecinsData } = await supabase.from('medecins').select('id');

      setStats({
        totalRdv: rdvData?.length || 0,
        rdvTermines: rdvData?.filter((rdv: any) => rdv.statut === 'termine').length || 0,
        rdvEnAttente: rdvData?.filter((rdv: any) => rdv.statut === 'en_attente').length || 0,
        totalPatients: patientsData?.length || 0,
        totalMedecins: medecinsData?.length || 0,
      });
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  // 🔐 Armer la réinitialisation de sécurité de tous les médecins
  const handleArmSecurityReset = async () => {
    setSecurityResetLoading(true);
    setSecurityResetResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/arm-medecins-security-reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erreur lors de l\'activation');
      }
      setSecurityResetResult({ type: 'success', msg: data.message || 'Réinitialisation activée.' });
      setSecurityResetModalOpen(false);
    } catch (e: any) {
      setSecurityResetResult({ type: 'error', msg: e.message || 'Erreur lors de l\'activation' });
    } finally {
      setSecurityResetLoading(false);
    }
  };

  const renderSecurityResetCard = () => (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-keyhole-line text-2xl text-amber-600"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-900">Réinitialiser la sécurité des médecins</p>
            <p className="mt-1 text-xs text-amber-700 leading-relaxed">
              Réinitialise le mot de passe de tous les médecins vers un mot de passe temporaire commun, efface leurs appareils de confiance et active la réinitialisation de mot de passe.
              Chaque médecin se connectera avec ce mot de passe temporaire puis sera guidé pour définir un nouveau mot de passe.
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setSecurityResetResult(null);
            setSecurityResetModalOpen(true);
          }}
          className="self-start sm:self-center shrink-0 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-shield-keyhole-line mr-1.5"></i>
          Armer la réinitialisation
        </button>
      </div>
      {securityResetResult && (
        <div className={`mt-3 rounded-lg px-4 py-2.5 text-sm ${securityResetResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {securityResetResult.msg}
        </div>
      )}
    </div>
  );

  const statItems = useMemo(() => [
    { label: 'Rendez-vous', value: stats.totalRdv || 0, route: '/admin/rendez-vous' },
    { label: 'Terminés', value: stats.rdvTermines || 0, route: '/admin/rendez-vous' },
    { label: 'En attente', value: stats.rdvEnAttente || 0 },
    { label: 'Patients', value: stats.totalPatients || 0, route: '/admin/patients' },
    { label: 'Médecins', value: stats.totalMedecins || 0, route: '/admin/invite-doctor' },
  ], [stats]);

  const personnelItems: ActionItem[] = [
    { title: 'Médecins', description: 'Inviter et gérer les médecins', route: '/admin/invite-doctor' },
    { title: 'Secrétaires', description: 'Comptes secrétaires, rendez-vous et disponibilités', route: '/admin/secretaires' },
    { title: 'Laborantins', description: 'Réception et traitement des analyses', route: '/admin/laborantins' },
    { title: 'Sages-Femmes', description: 'Consultations prénatales et bons laboratoire', route: '/admin/sage-femmes' },
    { title: 'Infirmiers', description: 'Comptes infirmiers et fiches de surveillance', route: '/admin/infirmiers' },
    { title: 'Comptables', description: 'Comptes comptables et gestion financière', route: '/admin/comptables' },
    ...(isSuperAdmin ? [{ title: 'Administrateurs', description: 'Accès complet à la plateforme', route: '/admin/administrateurs' }] : []),
  ];

  const patientsItems: ActionItem[] = [
    { title: 'Gestion des Patients', description: 'Superviser dossiers et consultations', route: '/admin/patients' },
    ...(isSuperAdmin ? [
      { title: 'Import CSV Patients', description: 'Importer en masse depuis un fichier CSV', route: '/admin/import-patients-csv' },
      { title: 'Numéros Réservés', description: 'Importer des numéros libres à recycler', route: '/admin/import-numeros-reserves' },
    ] : []),
    { title: 'Corrections Noms', description: 'Valider les corrections de patients temporaires', route: '/admin/corrections-patients' },
    { title: 'Dossiers Temporaires', description: 'Rechercher et compléter les dossiers', route: '/secretaire/gestion-patients' },
    ...(isSuperAdmin ? [
      { title: 'Base Médicaments', description: 'Importer le catalogue de médicaments', route: '/admin/medicaments' },
    ] : []),
  ];

  const activiteItems: ActionItem[] = [
    { title: 'Rendez-vous', description: 'Gérer les rendez-vous de tous les médecins', route: '/admin/rendez-vous' },
    { title: 'Consultations', description: 'Superviser et gérer les consultations', route: '/admin/consultations' },
    { title: 'Bons Laboratoire', description: 'Analyses en attente ou traitées', route: '/admin/bons-laboratoire' },
    { title: 'Suivis Médicaux', description: 'Superviser échanges et prescriptions', route: '/admin/suivis' },
    { title: 'Visioconférences', description: 'Historique et gestion des réunions', route: '/admin/video-meetings' },
    { title: 'Actualités', description: 'Publier actualités, événements et informations', route: '/admin/actualites' },
    { title: 'Bandeau d\'annonces', description: 'Messages défilants sur la page d\'accueil', route: '/admin/annonces' },
    { title: 'Messages Contact', description: 'Consulter les messages reçus', route: '/admin/messages-contact' },
  ];

  const systemeItems: ActionItem[] = [
    { title: 'Statistiques', description: 'Analyses et rapports d\'activité', route: '/admin/statistiques' },
    ...(isSuperAdmin ? [
      { title: 'Configuration', description: 'Paramètres globaux et maintenance', route: '/admin/configuration' },
      { title: '2FA & Sécurité', description: 'Email 2FA du personnel et blocage', route: '/admin/2fa' },
      { title: 'Accès (APDP)', description: 'Journalisation des accès aux données', route: '/admin/acces-logs' },
      { title: 'Connexions', description: 'Historique des sessions utilisateurs', route: '/admin/sessions' },
    ] : []),
    { title: 'Assurances', description: 'Gérer les assurances partenaires', route: '/admin/assurances' },
  ];

  const overviewItems: ActionItem[] = [
    { title: 'Rendez-vous', description: 'Gérer tous les rendez-vous', route: '/admin/rendez-vous' },
    { title: 'Patients', description: 'Rechercher et gérer les dossiers', route: '/admin/patients' },
    { title: 'Dossiers Temporaires', description: 'Compléter les dossiers en attente', route: '/secretaire/gestion-patients' },
    { title: 'Messages', description: 'Consulter les messages de contact', route: '/admin/messages-contact' },
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4 block"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!isAdminUser) return null;

  const activeSectionMeta = SECTIONS.find((s) => s.key === activeSection);

  const renderActionList = (items: ActionItem[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {items.map((item) => (
        <Link
          key={item.title}
          to={item.route}
          className="group flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-white px-5 py-4 transition-all duration-200 hover:border-clinic-blue/30 hover:bg-clinic-blue/[0.03]"
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 group-hover:text-clinic-blue-dark transition-colors">
              {item.title}
            </p>
            <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">{item.description}</p>
          </div>
          <i className="ri-arrow-right-s-line text-lg text-gray-300 group-hover:text-clinic-blue group-hover:translate-x-0.5 transition-all flex-shrink-0"></i>
        </Link>
      ))}
    </div>
  );

  const renderContent = () => {
    if (activeSection === 'overview') {
      return (
        <>
          {renderSmsAlert()}
          <SectionTitle title="Actions rapides" />
          {renderActionList(overviewItems)}
        </>
      );
    }
    if (activeSection === 'personnel') {
      return (
        <>
          {renderSecurityResetCard()}
          {renderActionList(personnelItems)}
        </>
      );
    }
    if (activeSection === 'patients') {
      return renderActionList(patientsItems);
    }
    if (activeSection === 'activite') {
      return renderActionList(activiteItems);
    }
    if (activeSection === 'systeme') {
      return renderActionList(systemeItems);
    }
    if (activeSection === 'file-attente') {
      return <AccueilFileAttente />;
    }
    return null;
  };

  const renderSmsAlert = () => (
    <div className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-amber-800">Crédits SMS épuisés</p>
        <p className="mt-0.5 text-xs text-amber-700">
          Brevo n'a plus de crédits. Rechargez pour rétablir les envois de codes par SMS.
        </p>
      </div>
      <a
        href="https://app.brevo.com/settings/billing"
        target="_blank"
        rel="noopener noreferrer"
        className="self-start rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 whitespace-nowrap"
      >
        Recharger
      </a>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFB]">
      <Header />

      <div className="flex">
        {/* === SIDEBAR (desktop) === */}
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-64 flex-shrink-0 flex-col border-r border-gray-100 bg-white lg:flex">
          <div className="px-6 py-6">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Administration
            </p>
          </div>
          <nav className="flex-1 space-y-1 px-3">
            {SECTIONS.map((section) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-sm font-medium transition-colors cursor-pointer ${
                  activeSection === section.key
                    ? 'bg-clinic-blue/10 text-clinic-blue-dark'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-clinic-blue-dark'
                }`}
              >
                <span className="whitespace-nowrap">{section.label}</span>
                {activeSection === section.key && (
                  <span className="h-5 w-1 rounded-full bg-clinic-blue"></span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* === MAIN CONTENT === */}
        <main className="min-w-0 flex-1">
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
            {/* Titre */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">{activeSectionMeta?.label}</h1>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap ${
                  isSuperAdmin
                    ? 'border border-red-200 bg-red-50 text-red-700'
                    : 'border border-amber-200 bg-amber-50 text-amber-700'
                }`}>
                  {isSuperAdmin ? 'Super Admin' : 'Admin'}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">{activeSectionMeta?.description}</p>
            </div>

            {/* Stats */}
            <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {statItems.map((stat) => (
                <div
                  key={stat.label}
                  onClick={() => stat.route && navigate(stat.route)}
                  className={`rounded-xl border border-gray-100 bg-white px-5 py-4 ${stat.route ? 'cursor-pointer transition-all duration-200 hover:border-clinic-blue/30' : ''}`}
                >
                  <p className="text-2xl font-bold text-clinic-blue-dark md:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-xs text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Contenu */}
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Modal de confirmation réinitialisation sécurité médecins */}
      {securityResetModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-keyhole-line text-4xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-amber-700 mb-2">Confirmer la réinitialisation</h3>
              <p className="text-gray-700">
                Vous êtes sur le point de réinitialiser le mot de passe de <strong>tous les médecins</strong> vers un mot de passe temporaire commun, d'effacer leurs appareils de confiance et d'activer la réinitialisation de mot de passe.
              </p>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-6">
              <p className="text-sm text-amber-800">
                <i className="ri-information-line mr-2"></i>
                Chaque médecin devra se reconnecter avec le mot de passe temporaire commun, ressaisir un code 2FA, puis définir un nouveau mot de passe via son email personnel.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={handleArmSecurityReset}
                disabled={securityResetLoading}
                className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {securityResetLoading ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Activation...
                  </span>
                ) : (
                  'Confirmer'
                )}
              </button>
              <button
                onClick={() => setSecurityResetModalOpen(false)}
                disabled={securityResetLoading}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <span className="h-px flex-1 bg-gray-100"></span>
    </div>
  );
}